import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { MenuItem } from "../configs/menuItems";
import { matchScope } from "../utils/string.utils";
import { AppIconType } from "../components/Core/AppIcon/AppIcon";

export interface Command {
	id: string;
	title: string; // Translation key
	translatedTitle: string; // Translated/display text
	route: string;
	icon: AppIconType;
	parentTitle?: string; // For submenu items (e.g., "ECONOMY")
}

export function useCommandSearch(
	menuItems: MenuItem[],
	scopes: string[],
	keyword: string
): Command[] {
	const { t } = useTranslation();

	return useMemo(() => {
		const commands: Command[] = [];

		menuItems.forEach((menuItem) => {
			// Check if user has permission to see this menu item
			const hasMenuPermission =
				!menuItem.scope || menuItem.scope.some((scopePattern) => matchScope(scopePattern, scopes));

			if (!hasMenuPermission) {
				return;
			}

			// Add top-level menu item if it has a route
			if (menuItem.route) {
				commands.push({
					id: `menu-${menuItem.title}`,
					title: menuItem.title,
					translatedTitle: t(menuItem.title as any),
					route: menuItem.route,
					icon: menuItem.icon,
				});
			}

			// Add submenu items
			if (menuItem.items) {
				menuItem.items.forEach((subItem) => {
					// Check if user has permission to see this submenu item
					const hasSubItemPermission =
						!subItem.scope ||
						subItem.scope.some((scopePattern) => matchScope(scopePattern, scopes));

					if (!hasSubItemPermission) {
						return;
					}

					commands.push({
						id: `submenu-${menuItem.title}-${subItem.title}`,
						title: subItem.title,
						translatedTitle: t(subItem.title as any),
						route: subItem.route,
						icon: menuItem.icon,
						parentTitle: t(menuItem.title as any),
					});
				});
			}
		});

		// Filter by keyword
		if (!keyword || keyword.length === 0) {
			return [];
		}

		const lowerKeyword = keyword.toLowerCase();
		const filtered = commands.filter((command) => {
			const translatedTitleMatch = command.translatedTitle.toLowerCase().includes(lowerKeyword);
			const titleKeyMatch = command.title.toLowerCase().includes(lowerKeyword);
			const parentTitleMatch = command.parentTitle?.toLowerCase().includes(lowerKeyword);

			return translatedTitleMatch || titleKeyMatch || parentTitleMatch;
		});

		// Limit to top 10 matches
		return filtered.slice(0, 10);
	}, [menuItems, scopes, keyword, t]);
}
